import { AHIcon, AHText } from "arkh";
import React, { useMemo } from "react";

import { DateTime } from "luxon";
import { EmptyResult } from "../components/EmptyResult";
import { ResultRow } from "./ResultRow";
import { graphql, useFragment } from "react-relay";
import { WidgetTaskMultipleRenderTaskNumber_taskExecutionNumber$key } from "./__generated__/WidgetTaskMultipleRenderTaskNumber_taskExecutionNumber.graphql";
import { WidgetTaskMultipleRenderTaskCheck_taskExecutionCheck$key } from "./__generated__/WidgetTaskMultipleRenderTaskCheck_taskExecutionCheck.graphql";
import { WidgetTaskMultipleRenderTaskText_taskExecutionText$key } from "./__generated__/WidgetTaskMultipleRenderTaskText_taskExecutionText.graphql";
import { TaskExecutionCheckSentimentIcon } from "../components/TaskExecutionCheckSentimentIcon";
import { WidgetTaskMultipleRenderTask_taskExecution$key } from "./__generated__/WidgetTaskMultipleRenderTask_taskExecution.graphql";
import { WidgetTaskMultipleRenderTaskContent_taskExecution$key } from "./__generated__/WidgetTaskMultipleRenderTaskContent_taskExecution.graphql";

export function WidgetTaskMultipleResponse(props: {
  taskExecution: WidgetTaskMultipleRenderTask_taskExecution$key;
  taskTitle: string;
  routineTitle: string;
}) {
  const now = DateTime.local();

  const taskExecution = useFragment(
    graphql`
      fragment WidgetTaskMultipleRenderTask_taskExecution on TaskExecution {
        ...WidgetTaskMultipleRenderTaskContent_taskExecution
        routineOccasion {
          endAt
        }
      }
    `,
    props.taskExecution
  );

  const isStale = useMemo(() => {
    if (!props.taskExecution) {
      return true;
    }
    const endAt = DateTime.fromISO(taskExecution.routineOccasion.endAt);
    return endAt < now;
  }, [now, props.taskExecution, taskExecution.routineOccasion.endAt]);
  return (
    <ResultRow
      stale={isStale}
      title={props.taskTitle}
      subTitle={props.routineTitle}
    >
      <ResultRowContent taskExecution={taskExecution} />
    </ResultRow>
  );
}

export function WidgetTaskMultipleResponseEmpty(props: {
  taskTitle: string;
  routineTitle: string;
}) {
  return (
    <ResultRow title={props.taskTitle} subTitle={props.routineTitle}>
      <EmptyResult size="small" />
    </ResultRow>
  );
}
function ResultRowContent(props: {
  taskExecution: WidgetTaskMultipleRenderTaskContent_taskExecution$key;
}) {
  const taskExecution = useFragment(
    graphql`
      fragment WidgetTaskMultipleRenderTaskContent_taskExecution on TaskExecution {
        ... on TaskExecutionCheck {
          __typename
          ...WidgetTaskMultipleRenderTaskCheck_taskExecutionCheck
        }
        ... on TaskExecutionNumber {
          __typename
          ...WidgetTaskMultipleRenderTaskNumber_taskExecutionNumber
        }
        ... on TaskExecutionText {
          __typename
          ...WidgetTaskMultipleRenderTaskText_taskExecutionText
        }
      }
    `,
    props.taskExecution
  );

  switch (taskExecution.__typename) {
    case "TaskExecutionNumber":
      return <WidgetTaskMultipleNumber taskExecution={taskExecution} />;
    case "TaskExecutionText":
      return <WidgetTaskMultipleText taskExecution={taskExecution} />;
    case "TaskExecutionCheck":
      return <WidgetTaskMultipleCheck taskExecution={taskExecution} />;
    default:
      return <EmptyResult size="small" />;
  }
}

function WidgetTaskMultipleCheck(props: {
  taskExecution: WidgetTaskMultipleRenderTaskCheck_taskExecutionCheck$key;
}) {
  const taskExecution = useFragment(
    graphql`
      fragment WidgetTaskMultipleRenderTaskCheck_taskExecutionCheck on TaskExecutionCheck {
        checkValue
      }
    `,
    props.taskExecution
  );

  if (!taskExecution.checkValue) {
    return <EmptyResult size="small" />;
  }
  return (
    <TaskExecutionCheckSentimentIcon
      sentiment={taskExecution.checkValue}
      size="small"
    />
  );
}

function WidgetTaskMultipleText(props: {
  taskExecution: WidgetTaskMultipleRenderTaskText_taskExecutionText$key;
}) {
  const taskExecution = useFragment(
    graphql`
      fragment WidgetTaskMultipleRenderTaskText_taskExecutionText on TaskExecutionText {
        textValue
      }
    `,
    props.taskExecution
  );
  return (
    <>
      {(taskExecution.textValue ?? "").length > 0 ? (
        <AHIcon type="text-response" size="medium" color="primary" />
      ) : (
        <EmptyResult size="small" />
      )}
    </>
  );
}

function WidgetTaskMultipleNumber(props: {
  taskExecution: WidgetTaskMultipleRenderTaskNumber_taskExecutionNumber$key;
}) {
  const taskExecution = useFragment(
    graphql`
      fragment WidgetTaskMultipleRenderTaskNumber_taskExecutionNumber on TaskExecutionNumber {
        numberValue
        formattedNumberValue
      }
    `,
    props.taskExecution
  );
  return (
    <>
      {taskExecution.numberValue !== undefined &&
      taskExecution.numberValue !== null ? (
        <AHText type="subtitle" size="detail" textAlign="center">
          {taskExecution.formattedNumberValue}
        </AHText>
      ) : (
        <EmptyResult size="small" />
      )}
    </>
  );
}
